/**
 * Local event queue + batched flush.
 *
 * Why a queue: track() is called from hot paths (button clicks, screen
 * views) and shouldn't block the UI on a network round-trip. Events go
 * into a local buffer, flushed in bursts.
 *
 * Flush triggers:
 *   - Buffer reaches batchSize (default 20) → flush immediately.
 *   - intervalMs of inactivity (default 1500ms) → flush idle batch.
 *   - flush() called explicitly (e.g. before page unload).
 *
 * Wave 1 hardening (v0.8.0+, the "bank-grade plumbing" pass):
 *
 *   - Exponential backoff with full jitter on flush failures. Respects
 *     server `Retry-After` headers (parsed onto CrossdeckError by the
 *     HTTP layer). Replaces the prior policy of "retry on the next
 *     idle window" which hot-looped against a flapping endpoint.
 *
 *   - Durable persistence. Events are written through to a
 *     `PersistentEventStore` (localStorage by default) so a hard
 *     browser crash / power loss / keepalive cap exceedance doesn't
 *     drop data. The next SDK boot replays the persisted queue.
 *
 *   - Per-batch `Idempotency-Key`. Same key is reused across retries
 *     of the SAME batch so the server can short-circuit duplicate work
 *     without inspecting bodies. The backend ALSO dedupes individual
 *     events via CH ReplacingMergeTree on `eventId`, so this is belt-
 *     and-suspenders.
 *
 *   - Property validation runs upstream in crossdeck.ts:track() — by
 *     the time an event lands in this queue, it's known to be safe
 *     to JSON.stringify.
 *
 * On a permanent network outage we keep retrying with bounded backoff;
 * we never drop events because of network failures alone. The only
 * drop path is the hard buffer cap (1000 events): once exceeded we
 * evict the OLDEST events and increment `dropped` so the developer
 * can see the loss in `diagnostics()`.
 */

import type { HttpClient } from "./http";
import type { EventProperties, IngestResponse } from "./types";
import type { CrossdeckError } from "./errors";
import { RetryPolicy, type RetryPolicyOptions } from "./retry-policy";
import { PersistentEventStore } from "./event-storage";
import { randomChars } from "./identity";

const HARD_BUFFER_CAP = 1000;

export interface QueuedEvent {
  eventId: string;
  name: string;
  timestamp: number;
  /**
   * Event Envelope v1 §3 — per-session monotonic sequence number.
   * Assigned synchronously at track() time from the session-scoped counter
   * (AutoTracker.nextSeq()), reset to 0 at session.started. The
   * deterministic tiebreak for events sharing a timestamp. Persisted on
   * disk alongside the event so a delayed flush across a
   * background/foreground cycle keeps its original seq.
   */
  seq: number;
  /**
   * Event Envelope v1 §4 — standardized device/platform context,
   * promoted OUT of `properties` into one named object. Keys: os,
   * osVersion, appVersion, sdkName, sdkVersion, locale, timezone,
   * browser, browserVersion. App-supplied properties stay in `properties`.
   */
  context: Record<string, string>;
  properties: EventProperties;
  // identity hint — at least anonymousId is always set
  developerUserId?: string;
  anonymousId?: string;
  crossdeckCustomerId?: string;
}

export interface BatchEnvelope {
  appId: string;
  environment: "production" | "sandbox";
  sdk: { name: string; version: string };
}

export interface EventQueueConfig {
  http: HttpClient;
  batchSize: number;
  intervalMs: number;
  /**
   * Returns the NorthStar §13.1 envelope to attach to each batch POST.
   * It's a function (not a value) so a future call to setDebugMode or a
   * config swap can update the envelope without re-instantiating the
   * queue.
   */
  envelope: () => BatchEnvelope;
  /** Schedule a function to run after `ms` ms. Default: setTimeout. Override for tests. */
  scheduler?: (fn: () => void, ms: number) => () => void;
  /** Called when the SDK drops events because the buffer is full. */
  onDrop?: (dropped: number) => void;
  /** Called once after the first successful flush — drives the §16 "First event sent" signal. */
  onFirstFlushSuccess?: () => void;
  /**
   * Durable persistence. When supplied, every buffer mutation is
   * written through to the store; on construction, persisted events
   * are loaded back into the buffer. Omitting this is fine for tests
   * and Node consumers — the queue falls back to in-memory only.
   */
  persistentStore?: PersistentEventStore;
  /** Retry policy overrides for failed flushes. */
  retry?: RetryPolicyOptions;
  /**
   * Called whenever an item is added to the buffer or removed by a
   * successful flush. Exposed so the host SDK can surface live queue
   * stats via diagnostics() without polling.
   */
  onBufferChange?: (size: number) => void;
  /**
   * Surface for the SDK's debug logger to record retry scheduling +
   * persistence events. Fired async — never throws.
   */
  onRetryScheduled?: (info: {
    delayMs: number;
    consecutiveFailures: number;
    retryAfterMs?: number;
    lastError: string;
  }) => void;
  /**
   * Fired when the queue DROPS a batch because the server returned a
   * permanent 4xx (anything except 408 Request Timeout / 429 Too Many
   * Requests). The host SDK should surface this loudly — pre-fix the
   * queue retried 4xx errors forever with the same Idempotency-Key,
   * silently growing the backlog while the customer thought events
   * were landing. Common causes:
   *   - 401: publishable key revoked / rotated
   *   - 403: lacking permission for the project
   *   - 400/422: malformed batch (schema mismatch, oversized event)
   *   - 404: endpoint doesn't exist (typo'd baseUrl)
   */
  onPermanentFailure?: (info: {
    status: number;
    droppedCount: number;
    lastError: string;
  }) => void;
  /**
   * Fired when the server PARKS this SDK with HTTP 426 Upgrade Required
   * (code `sdk_version_unsupported`) — the SDK's wire dialect is too old
   * for the server to accept. This is the third outcome, distinct from
   * retry (transient) and drop (invalid): the data is GOOD, only the
   * format is stale. The queue holds the events durably, stops flushing
   * (the same payload will fail until the app upgrades the SDK), and on
   * the next boot AFTER upgrade the rehydrated queue delivers them — so
   * "paused, not lost — held on-device, resumes on upgrade" is literally
   * true. The host SDK surfaces this to the developer's own console once
   * and to the dashboard (via the heartbeat) so both channels carry the
   * same cure. `minVersion` is the server's required floor when supplied.
   */
  onParked?: (info: { minVersion?: string; surface?: string }) => void;
}

export interface EventQueueStats {
  buffered: number;
  dropped: number;
  inFlight: number;
  lastFlushAt: number;
  lastError: string | null;
  /** Consecutive flush failures since the last success. */
  consecutiveFailures: number;
  /** Set when the next flush is scheduled by the retry policy. */
  nextRetryAt: number | null;
}

export class EventQueue {
  private buffer: QueuedEvent[] = [];
  /**
   * In-flight events that have been spliced from `buffer` for the
   * current batch but haven't yet been confirmed (success or permanent
   * failure). On a retry-driven re-flush we re-use this slot alongside
   * `pendingBatchId` so the Stripe-style Idempotency-Key is preserved
   * across retries of the SAME logical batch.
   *
   * Pre-fix the splice cleared the buffer AND we immediately
   * `persistent.save(empty)` BEFORE awaiting the network call — a
   * crash mid-flight wiped the persisted blob and the batch was lost
   * with no replay on next boot. Now the persisted blob always carries
   * `[...pendingBatch, ...buffer]` so the in-flight events survive
   * until the server confirms them.
   *
   * Pre-fix every retry attempt also minted a NEW batchId via
   * `splice + mintBatchId`, defeating the backend's
   * `Idempotency-Key` dedup. Reuse via this slot brings web in
   * lockstep with node (which already had the field).
   */
  private pendingBatch: QueuedEvent[] | null = null;
  private pendingBatchId: string | null = null;
  private dropped = 0;
  private inFlight = 0;
  private lastFlushAt = 0;
  private lastError: string | null = null;
  private cancelTimer: (() => void) | null = null;
  private firstFlushFired = false;
  private nextRetryAt: number | null = null;
  /**
   * PARK state (HTTP 426 / `sdk_version_unsupported`). Once parked, the
   * queue stops flushing — the server has told us our wire dialect is too
   * old, and retrying the same payload only burns the user's battery and
   * bandwidth and drips pointless rejects into the server logs until the
   * app ships an upgraded SDK. The held events stay durable (persisted)
   * and are delivered by the next boot's rehydrate, post-upgrade. Parked
   * is per-instance: a fresh boot (the upgraded build) starts unparked.
   */
  private parked = false;
  /** One developer-facing console warning per instance — never per-event spam. */
  private parkWarned = false;
  private readonly retry: RetryPolicy;
  private readonly persistent: PersistentEventStore | null;

  constructor(private readonly cfg: EventQueueConfig) {
    this.retry = new RetryPolicy(cfg.retry ?? {});
    this.persistent = cfg.persistentStore ?? null;

    // Rehydrate any events left over from a prior session (crash, hard
    // close, keepalive cap exceeded). Eventid-based dedup at the server
    // means re-sending an event that may have already landed is safe.
    // Rehydrated events land in `buffer` — the "was in-flight" axis
    // doesn't survive a crash, so they're treated like fresh enqueues.
    if (this.persistent) {
      const restored = this.persistent.load();
      if (restored.length > 0) {
        // Apply the same hard cap on rehydrate — defends against a
        // malicious / corrupted blob with a million entries.
        if (restored.length > HARD_BUFFER_CAP) {
          this.dropped += restored.length - HARD_BUFFER_CAP;
          this.buffer = restored.slice(restored.length - HARD_BUFFER_CAP);
        } else {
          this.buffer = restored;
        }
        this.cfg.onBufferChange?.(this.buffer.length);
        // Schedule an immediate idle flush so rehydrated events land
        // on the next tick — even if no new track() call comes in.
        this.scheduleIdleFlush();
      }
    }
  }

  enqueue(event: QueuedEvent): void {
    this.buffer.push(event);
    if (this.buffer.length > HARD_BUFFER_CAP) {
      const overflow = this.buffer.length - HARD_BUFFER_CAP;
      this.buffer.splice(0, overflow);
      this.dropped += overflow;
      this.cfg.onDrop?.(overflow);
    }
    this.cfg.onBufferChange?.(this.buffer.length);
    this.persistAll();
    if (this.buffer.length >= this.cfg.batchSize) {
      void this.flush();
    } else {
      this.scheduleIdleFlush();
    }
  }

  /**
   * Flush the buffer to /v1/events. Resolves when the network call
   * completes (success or failure). On a retryable failure the batch
   * stays in `pendingBatch` for the next scheduled retry — the SAME
   * batch with the SAME `Idempotency-Key` is re-sent (Stripe pattern).
   *
   * Three terminal states from one call:
   *   - 2xx success: pendingBatch cleared, persisted state collapses to
   *     just `buffer` (any new events that arrived during in-flight).
   *   - 4xx permanent (except 408/429): pendingBatch DROPPED, `dropped`
   *     counter increments, a `permanent_failure` signal fires. The
   *     server is telling us our request is malformed / our key is
   *     revoked / we lack permission — retrying with the same key
   *     forever just grows the queue while the customer thinks events
   *     are landing.
   *   - 5xx / network / 408 / 429: pendingBatch + batchId stay; backoff
   *     schedules a retry; the next `flush()` re-uses both.
   *
   * `options.keepalive` marks the underlying fetch as keepalive so the
   * browser keeps the request alive past page unload. Use for terminal
   * flushes (pagehide / visibilitychange→hidden / beforeunload).
   */
  async flush(options: { keepalive?: boolean } = {}): Promise<IngestResponse | null> {
    // PARK hush: once the server has rejected our wire dialect as too old
    // (426), every flush of the same-format payload will fail identically.
    // Hold — don't flush — until the next boot on an upgraded SDK. The
    // events stay buffered + persisted, so they deliver on that boot.
    if (this.parked) return null;
    // Resume an in-flight batch retry path: if we already have a
    // pending batch (prior flush failed, retry timer / caller is
    // re-invoking), re-attempt with the SAME batchId. Stripe
    // Idempotency-Key reuse contract.
    let batch: QueuedEvent[];
    let batchId: string;
    if (this.pendingBatch !== null && this.pendingBatchId !== null) {
      batch = this.pendingBatch;
      batchId = this.pendingBatchId;
    } else {
      if (this.buffer.length === 0) return null;
      batch = this.buffer.splice(0);
      batchId = this.mintBatchId();
      this.pendingBatch = batch;
      this.pendingBatchId = batchId;
      this.inFlight += batch.length;
      this.cfg.onBufferChange?.(this.buffer.length);
      // Persisted state continues to include this batch via persistAll()
      // until the server confirms it — that's the durability fix.
      this.persistAll();
    }
    this.cancelTimerIfSet();
    this.nextRetryAt = null;

    try {
      const env = this.cfg.envelope();
      const result = await this.cfg.http.request<IngestResponse>("POST", "/events", {
        body: {
          // Event Envelope v1 batch envelope (backend/docs/
          // event-envelope-spec-v1.md §1). `envelopeVersion` is the
          // schema/wire version the server parses against ("can I parse
          // this?") and is DISTINCT from `sdk.version` ("which build is in
          // the wild?") — two questions, two fields, never conflated. The
          // backend refuses payloads with a missing/unknown envelopeVersion.
          envelopeVersion: 1,
          appId: env.appId,
          environment: env.environment,
          sdk: env.sdk,
          events: batch,
        },
        keepalive: options.keepalive === true,
        idempotencyKey: batchId,
      });
      this.lastFlushAt = Date.now();
      this.lastError = null;
      this.inFlight -= batch.length;
      this.pendingBatch = null;
      this.pendingBatchId = null;
      this.retry.recordSuccess();
      // Persisted blob collapses to just `buffer` (which may include
      // new enqueues that arrived while this batch was in flight).
      this.persistAll();
      if (!this.firstFlushFired) {
        this.firstFlushFired = true;
        this.cfg.onFirstFlushSuccess?.();
      }
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.lastError = message;

      // PARK (HTTP 426 / `sdk_version_unsupported`) — the THIRD outcome.
      // Checked BEFORE the permanent-4xx drop so a version-rejection never
      // falls into the drop path. The data is good; only the dialect is
      // old. Keep every held event — fold the in-flight batch back to the
      // FRONT of the buffer (oldest-first) so it persists and the next
      // (upgraded) boot's rehydrate delivers it — then hush (no retry
      // scheduled; the flush guard above blocks further attempts).
      if (isVersionRejected(err)) {
        this.parked = true;
        this.buffer = [...batch, ...this.buffer];
        if (this.buffer.length > HARD_BUFFER_CAP) {
          const overflow = this.buffer.length - HARD_BUFFER_CAP;
          this.buffer.splice(0, overflow); // FIFO: evict oldest, keep newest 1000
          this.dropped += overflow;
        }
        this.pendingBatch = null;
        this.pendingBatchId = null;
        this.inFlight -= batch.length;
        this.persistAll();
        this.cfg.onBufferChange?.(this.buffer.length);
        const minVersion = versionRejectionFloor(err);
        if (!this.parkWarned) {
          this.parkWarned = true;
          // ONE developer-facing console line — the terminal-side cure that
          // reaches them mid-debug, paired with the dashboard banner.
          console.warn(
            "[Crossdeck] SDK outdated — the server is no longer accepting this " +
              "version's event format. Your events are PARKED on-device (held, " +
              "not lost) and will deliver automatically once you update " +
              `@cross-deck/web${minVersion ? ` to >= ${minVersion}` : ""} and redeploy.`,
          );
        }
        this.cfg.onParked?.({ minVersion, surface: versionRejectionSurface(err) });
        return null;
      }

      // Permanent failures (4xx except 408/429) are NOT retryable. The
      // server is telling us our request is malformed (400/422), our
      // key is revoked (401), we lack permission (403), or the endpoint
      // doesn't exist (404). Retrying with the same Idempotency-Key
      // forever just grows the queue silently while the customer
      // thinks events are landing. Drop the batch loudly.
      if (isPermanent4xx(err)) {
        const droppedCount = batch.length;
        this.pendingBatch = null;
        this.pendingBatchId = null;
        this.inFlight -= droppedCount;
        this.dropped += droppedCount;
        this.persistAll();
        this.cfg.onDrop?.(droppedCount);
        this.cfg.onPermanentFailure?.({
          status: (err as { status?: number }).status ?? 0,
          droppedCount,
          lastError: message,
        });
        return null;
      }

      // Retryable failure (5xx / network / 408 / 429). pendingBatch +
      // pendingBatchId stay set; the next scheduler-driven flush re-uses
      // both. Persisted state is unchanged from the entry path — it
      // still includes `[...pendingBatch, ...buffer]`.
      const retryAfterMs = extractRetryAfterMs(err);
      const delay = this.retry.nextDelay(retryAfterMs);
      this.scheduleRetry(delay);
      this.cfg.onRetryScheduled?.({
        delayMs: delay,
        consecutiveFailures: this.retry.consecutiveFailures,
        retryAfterMs,
        lastError: message,
      });
      return null;
    }
  }

  /** Cancel any pending timer and clear in-memory state. Wipes durable store too. */
  reset(): void {
    this.cancelTimerIfSet();
    this.nextRetryAt = null;
    this.buffer = [];
    this.pendingBatch = null;
    this.pendingBatchId = null;
    this.dropped = 0;
    this.inFlight = 0;
    this.lastError = null;
    this.retry.recordSuccess();
    this.persistent?.clear();
    this.cfg.onBufferChange?.(0);
    // Note: we deliberately do NOT reset firstFlushFired — the
    // "First event sent" signal is a one-time onboarding moment per
    // SDK instance lifetime, not per-identity.
  }

  getStats(): EventQueueStats {
    return {
      // `buffered` counts events waiting for their FIRST flush. The
      // in-flight pendingBatch (retrying) is tracked separately via
      // `inFlight` — surfacing both lets diagnostics show "we have
      // events stuck retrying" distinct from "new events arriving".
      buffered: this.buffer.length,
      dropped: this.dropped,
      inFlight: this.inFlight,
      lastFlushAt: this.lastFlushAt,
      lastError: this.lastError,
      consecutiveFailures: this.retry.consecutiveFailures,
      nextRetryAt: this.nextRetryAt,
    };
  }

  /**
   * The Idempotency-Key of the in-flight pending batch (if any).
   * Exposed for testing the Stripe-style retry-reuse contract.
   * Production callers don't need this.
   */
  get pendingIdempotencyKey(): string | null {
    return this.pendingBatchId;
  }

  /**
   * Persist `[...pendingBatch, ...buffer]` — the full set of
   * not-yet-confirmed events. The next boot rehydrates this exact set
   * into `buffer` and replays. The server dedups via eventId
   * (ReplacingMergeTree on the warehouse side), so re-sending an event
   * that may have already landed is safe.
   */
  private persistAll(): void {
    if (!this.persistent) return;
    if (this.pendingBatch === null) {
      this.persistent.save(this.buffer);
      return;
    }
    this.persistent.save([...this.pendingBatch, ...this.buffer]);
  }

  // ---------- internal scheduling ----------

  private scheduleIdleFlush(): void {
    this.cancelTimerIfSet();
    const sched = this.cfg.scheduler ?? defaultScheduler;
    this.cancelTimer = sched(() => {
      void this.flush();
    }, this.cfg.intervalMs);
  }

  private scheduleRetry(delayMs: number): void {
    this.cancelTimerIfSet();
    this.nextRetryAt = Date.now() + delayMs;
    const sched = this.cfg.scheduler ?? defaultScheduler;
    this.cancelTimer = sched(() => {
      void this.flush();
    }, delayMs);
  }

  private cancelTimerIfSet(): void {
    if (this.cancelTimer) {
      this.cancelTimer();
      this.cancelTimer = null;
    }
  }

  private mintBatchId(): string {
    return `batch_${Date.now().toString(36)}${randomChars(10)}`;
  }
}

function extractRetryAfterMs(err: unknown): number | undefined {
  if (err && typeof err === "object" && "retryAfterMs" in err) {
    const v = (err as CrossdeckError).retryAfterMs;
    return typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : undefined;
  }
  return undefined;
}

/**
 * True when the error represents a permanent 4xx response that
 * SHOULDN'T be retried. Excludes 408 Request Timeout and 429 Too Many
 * Requests — both indicate transient state where the SAME request
 * (with the SAME Idempotency-Key) can succeed on a retry.
 *
 * Anything that isn't a CrossdeckError-shaped object with a numeric
 * status field returns false (network errors / fetch failures fall
 * here — those ARE retryable). Conservative default: only flag as
 * permanent when we have strong evidence from the server.
 */
function isPermanent4xx(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const status = (err as { status?: unknown }).status;
  if (typeof status !== "number" || !Number.isFinite(status)) return false;
  if (status < 400 || status >= 500) return false;
  if (status === 408 || status === 429) return false;
  // 426 is PARK, never drop — handled by isVersionRejected before this is
  // ever reached, but excluded here too so no path can drop a parkable batch.
  if (status === 426) return false;
  return true;
}

/**
 * True when the server PARKED this SDK: HTTP 426 Upgrade Required, the
 * status code invented for exactly this — "your client is too old."
 * Unambiguous (no proxy or middleware emits 426 spuriously, unlike
 * 400/422), and corroborated by the machine code `sdk_version_unsupported`
 * so a status-rewriting layer can't hide it. Distinct from a permanent
 * 4xx drop: the data is good, only the wire dialect is stale.
 */
function isVersionRejected(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  if ((err as { status?: unknown }).status === 426) return true;
  return (err as { code?: unknown }).code === "sdk_version_unsupported";
}

/** Server-supplied required version floor from the 426 body, if present. */
function versionRejectionFloor(err: unknown): string | undefined {
  const v = (err as { minVersion?: unknown } | null)?.minVersion;
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

/** Server-supplied surface id from the 426 body, if present. */
function versionRejectionSurface(err: unknown): string | undefined {
  const v = (err as { surface?: unknown } | null)?.surface;
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

function defaultScheduler(fn: () => void, ms: number): () => void {
  // Use unref()-style behaviour where supported so a pending flush doesn't
  // block Node from exiting. setTimeout in browsers ignores .unref() —
  // that's fine.
  const id = setTimeout(fn, ms);
  if (typeof (id as unknown as { unref?: () => void }).unref === "function") {
    try {
      (id as unknown as { unref: () => void }).unref();
    } catch {
      // ignore — unref is best-effort
    }
  }
  return () => clearTimeout(id);
}
